<template>
  <el-row :gutter="15">
    <el-col
      :xs="7"
      :sm="6"
      :md="6"
      :lg="3"
      :xl="3"
    >
      <Filters
        v-model="filters"
        :resource="$sdk.pickup"
        :primary-fields="[ 'booking_reference', 'account', 'carrier', 'consignments.con_full', 'sender', 'receiver', 'customer_reference', 'carrier_reference', 'status', 'customer_pickup_date' ]"
        :setup-fields="{ customer_pickup_date: setupCustomerPickupDateFilterable }"
        @sort="sort = $event; reload()"
        @apply="reload"
        @ready="load"
      >
        <template v-slot:sender.input="{ filterable, apply }">
          <el-input
            v-model="filterable.filter.value"
            clearable
            size="small"
            class="w-100"
            @keyup.enter.native="apply"
          />
        </template>

        <template v-slot:receiver.input="{ filterable, apply }">
          <el-input
            v-model="filterable.filter.value"
            clearable
            size="small"
            class="w-100"
            @keyup.enter.native="apply"
          />
        </template>
      </Filters>
    </el-col>

    <el-col
      :xs="17"
      :sm="18"
      :md="18"
      :lg="21"
      :xl="21"
    >
      <el-card
        v-loading="isLoading"
        shadow="never"
      >
        <PickupsTable :pickups="pickups">
          <template v-slot:peek="{ pickup }">
            <PickupPeek :pickup="pickup" />
          </template>

          <template v-slot:reference="{ pickup }">
            <router-link :to="{ name: 'customerservice-pickups-pickup', params: { pickupId: pickup.id } }">
              {{ pickup.booking_reference }}
            </router-link>
          </template>

          <template
            v-if="canUpdate"
            v-slot:actions="{ pickup }"
          >
            <el-dropdown
              split-button
              size="mini"
              trigger="click"
              @command="handleActionCommand"
              @click="canConfirmPickup(pickup) ? promptPickupConfirmationReference(pickup) : reBookPickup(pickup)"
            >
              {{ canConfirmPickup(pickup) ? 'Confirm' : 'Re-book' }}
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  :command="{ action: promptPickupCancellationReason, pickup}"
                  :disabled="!canCancelPickup(pickup)"
                >
                  Cancel
                </el-dropdown-item>

                <el-dropdown-item
                  v-if="canConfirmPickup(pickup)"
                  :command="{ action: reBookPickup, pickup}"
                >
                  Re-book
                </el-dropdown-item>

                <el-dropdown-item
                  :command="{ action: duplicatePickup, pickup}"
                >
                  Duplicate
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </PickupsTable>

        <el-pagination
          class="text-center pt-3"
          :current-page.sync="page"
          :page-size.sync="perPage"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pickups.total"
          @size-change="reload"
          @current-change="reload"
        />
      </el-card>
    </el-col>
  </el-row>
</template>

<script>
import PickupsTable from '@/components/Application/CustomerService/Pickups/Table'
import PickupPeek from '@/components/Application/CustomerService/Pickups/Pickup/Peek'
import PickupCreateDialog from '@/components/Application/CustomerService/Pickups/Pickup/CreateDialog'
import { statusAllows } from '@/components/Application/CustomerService/Pickups/Pickup/PickupFunctions'
import Filters from '@/components/Filters/Simplified'
import { IS_BETWEEN } from '@/plugins/sdk/Filter'
import { CREATED, UPDATED, DELETED, RESTORED } from '@/plugins/sdk/Events'
import Vue from 'vue'
import { CREATE, UPDATE } from '@/plugins/user'

export default {
  components: {
    PickupsTable,
    PickupPeek,
    Filters
  },
  data () {
    return {
      title: 'Pickups',
      actions: [
        {
          hidden: this.$user.cannot(CREATE, this.$sdk.pickup),
          name: 'Create',
          icon: ['fal', 'plus'],
          event: () => {
            const pickup = new this.$sdk.pickup()

            pickup.customer_pickup_date = new Date()
            pickup.customer_ready_time = new Date()
            pickup.collection_name = 'DESPATCH'
            pickup.sender = new this.$sdk.address()
            pickup.receiver = new this.$sdk.address()
            pickup.items.push(new this.$sdk.pickup_item())

            this.createPickup(pickup)
          }
        },
        {
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: this.reload
        }
      ],
      alfred: [
        {
          name: 'Book Pickup',
          description: 'Book a new pickup',
          keywords: ['pickup', 'book', 'schedule'],
          icon: ['fal', 'calendar-plus'],
          hidden: false,
          handler: () => {
            const pickup = new this.$sdk.pickup()

            pickup.customer_pickup_date = new Date()
            pickup.customer_ready_time = new Date()
            pickup.collection_name = 'DESPATCH'
            pickup.sender = new this.$sdk.address()
            pickup.receiver = new this.$sdk.address()
            pickup.items.push(new this.$sdk.pickup_item())

            this.createPickup(pickup)
          }
        },
        {
          name: 'Refresh Pickups',
          description: 'Refresh the pickups list',
          keywords: ['pickup', 'refresh', 'reload'],
          icon: ['fal', 'sync'],
          hidden: false,
          handler: this.reload
        }
      ],
      isLoading: true,
      searchAbortController: undefined,
      pickupCreateDialogVisible: false,
      pickupCreateDialogInstance: undefined,
      page: 1,
      perPage: 30,
      filters: [],
      pickups: [],
      sort: {
        field: 'id',
        direction: 'desc'
      }
    }
  },
  computed: {
    canUpdate () {
      return this.$user.can(UPDATE, this.$sdk.pickup)
    }
  },
  watch: {
    pickupCreateDialogVisible (visible) {
      if (!visible && typeof this.pickupCreateDialogInstance !== 'undefined') {
        this.pickupCreateDialogInstance.$destroy()
        this.pickupCreateDialogInstance.$el.remove()
        this.pickupCreateDialogInstance = undefined
      }
    }
  },
  mounted () {
    this
      .$sdk
      .pickup
      .on([ CREATED, UPDATED, DELETED, RESTORED ], this.reload, this)
  },
  methods: {
    setupCustomerPickupDateFilterable (filterable) {
      filterable.filter.operator = IS_BETWEEN
    },
    // async load () {
    //   this.isLoading = true
    //   if (typeof this.searchAbortController !== 'undefined') {
    //     this.searchAbortController.abort()
    //   }
    //
    //   this.searchAbortController = new AbortController()
    //   this.pickups = await this
    //     .$sdk
    //     .pickup
    //     .list(request => {
    //       request.filters = [
    //         new Filter('type', IS_NOT, 'reserved'),
    //         ...this.filters
    //       ]
    //       request.include = [
    //         'account',
    //         'carrier',
    //         'carrier_service',
    //         'profile',
    //         'items.type',
    //         'consignments.items.type',
    //         'notes'
    //       ]
    //       request.order = 'created_at:desc'
    //     }, this.searchAbortController.signal)
    //
    //   this.searchAbortController = undefined
    //   this.isLoading = false
    // },
    // async reload () {
    //   this.isLoading = true
    //
    //   await this
    //     .pickups
    //     .reload(request => {
    //       request.filters = [
    //         new Filter('type', IS_NOT, 'reserved'),
    //         ...this.filters
    //       ]
    //     })
    //
    //   this.isLoading = false
    // },
    async load () {
      this.isLoading = true

      if (typeof this.searchAbortController !== 'undefined') {
        this.searchAbortController.abort()
      }

      this.searchAbortController = new AbortController()

      try {
        this.pickups = await this
          .$sdk
          .pickup
          .list(request => {
            request.filters = this.filters
            request.include = [
              'account',
              'carrier',
              'carrier_service',
              'profile',
              'items.type',
              'consignments.items.type',
              'notes'
            ]
            request.order = `${this.sort.field}:${this.sort.direction}`
            request.page = this.page
            request.limit = this.perPage
          }, this.searchAbortController.signal)
      } catch (error) {
        // console.log('error', error)
      } finally {
        this.searchAbortController = undefined
        this.isLoading = false
      }
    },
    async reload () {
      this.isLoading = true

      if (typeof this.searchAbortController !== 'undefined') {
        this.searchAbortController.abort()
      }

      this.searchAbortController = new AbortController()

      try {
        this.pickups = await this
          .$sdk
          .pickup
          .list(request => {
            request.filters = this.filters
            request.include = [
              'account',
              'carrier',
              'carrier_service',
              'profile',
              'items.type',
              'consignments.items.type',
              'notes'
            ]
            request.order = `${this.sort.field}:${this.sort.direction}`
            request.page = this.page
            request.limit = this.perPage
          }, this.searchAbortController.signal)
      } catch (error) {
        // console.log('error', error)
      } finally {
        this.searchAbortController = undefined
        this.isLoading = false
      }
    },
    canCancelPickup (pickup) {
      return statusAllows(pickup.status, 'cancelled')
    },
    canConfirmPickup (pickup) {
      return statusAllows(pickup.status, 'confirmed')
    },
    handleActionCommand ({ action, pickup }) {
      action(pickup)
    },
    promptPickupCancellationReason (pickup) {
      this
        .$prompt(
          'Enter a Cancellation Reason',
          `Cancel Pickup ${pickup.booking_reference}`,
          {
            inputValidator: value => {
              return value && value.length > 0 ? true : 'A cancellation reason is required'
            },
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            callback: (action, instance) => {
              if (action === 'confirm') {
                this.cancelPickup(pickup, `Cancellation reason: ${instance.inputValue}`)
              }
            }
          }
        )
    },
    promptPickupConfirmationReference (pickup) {
      this
        .$prompt(
          'Enter a Confirmation Reference',
          `Confirm Pickup ${pickup.booking_reference}`,
          {
            inputValidator: value => {
              return value && value.length > 0 ? true : 'A confirmation reference is required'
            },
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            callback: (action, instance) => {
              if (action === 'confirm') {
                this.confirmPickup(pickup, instance.inputValue)
              }
            }
          }
        )
    },
    async cancelPickup (pickup, reason) {
      this.isLoading = true

      await this
        .$sdk
        .note
        .create({
          content: reason,
          resource: 'pickup',
          resource_uuid: pickup.id
        })

      try {
        await pickup.update(
          request => {
            request.body = { status: 'cancelled' }
          }
        )

        this.$message.success(`Updated Pickup ${pickup.booking_reference}`)
      } catch (e) {
        this.isLoading = false

        throw e
      }
    },
    async confirmPickup (pickup, reference) {
      this.isLoading = true

      try {
        await pickup.update(
          request => {
            request.body = { carrier_reference: reference, status: 'confirmed' }
          }
        )

        this.$message.success(`Updated Pickup ${pickup.booking_reference}`)
      } catch (e) {
        this.isLoading = false

        throw e
      }
    },
    async reBookPickup (pickup) {
      pickup = pickup.clone()

      await pickup.refresh(request => {
        request.include = [
          'account.carrier_settings.carrier',
          'account.carrier_service_settings.carrier_service.carrier',
          'account.teams.shipper_setting.address',
          'profile',
          'carrier',
          'carrier_service',
          'sender',
          'receiver',
          'consignments.items.type',
          'items.type',
          'customer_time_zone',
          'team.shipper_setting.address',
          'events.profile'
        ]
      })

      const reBookedPickup = pickup.replicate()

      reBookedPickup.id = undefined
      reBookedPickup.booking_reference = undefined
      reBookedPickup.carrier_reference = undefined
      reBookedPickup.customer_pickup_date = new Date()
      reBookedPickup.profile = undefined
      reBookedPickup.status = undefined
      reBookedPickup.previous_pickup = pickup
      reBookedPickup.receiver.id = undefined
      reBookedPickup.sender.id = undefined

      reBookedPickup
        .items
        .map(item => {
          item.id = undefined
          item.created_at = undefined
          item.updated_at = undefined
        })

      reBookedPickup
        .notes
        .map(note => {
          note.id = undefined
          note.created_at = undefined
          note.updated_at = undefined
          note.creator = undefined
        })

      this.createPickup(reBookedPickup)
    },
    createPickup (pickup) {
      const PickupCreateDialogClass = Vue.extend(PickupCreateDialog)

      this.pickupCreateDialogInstance = new PickupCreateDialogClass({
        propsData: {
          pickup: pickup
        }
      })

      this
        .pickupCreateDialogInstance
        .$on('destroy', () => {
          this.pickupCreateDialogVisible = false
        })

      this
        .pickupCreateDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.pickupCreateDialogInstance.$el)

      this.pickupCreateDialogVisible = true
    },
    async duplicatePickup (pickup) {
      pickup = pickup.clone()

      await pickup.refresh(request => {
        request.include = [
          'account.carrier_settings.carrier',
          'account.carrier_service_settings.carrier_service.carrier',
          'account.teams.shipper_setting.address',
          'profile',
          'carrier',
          'carrier_service',
          'sender',
          'receiver',
          'customer_time_zone',
          'team.shipper_setting.address',
          'events.profile'
        ]
      })

      const duplicatePickup = pickup.replicate()

      duplicatePickup.id = undefined
      duplicatePickup.booking_reference = undefined
      duplicatePickup.carrier_reference = undefined
      duplicatePickup.customer_pickup_date = new Date()
      duplicatePickup.profile = undefined
      duplicatePickup.status = undefined
      duplicatePickup.previous_pickup = undefined
      duplicatePickup.consignments = undefined
      duplicatePickup.items = undefined
      duplicatePickup.customer_reference = undefined
      duplicatePickup.collection_name = undefined
      duplicatePickup.special_instructions = undefined
      duplicatePickup.cost_centre = undefined
      duplicatePickup.carrier_reference = undefined
      duplicatePickup.receiver.id = undefined
      duplicatePickup.sender.id = undefined
      duplicatePickup.events = undefined
      duplicatePickup.notes = undefined

      this.createPickup(duplicatePickup)
    }
  }
}
</script>

<style lang="scss" scoped>
  ::v-deep .el-button--mini {
    border-left: 1px solid #DCDFE6;

    .el-dropdown__icon {
      font-size: 11px;
    }
  }
</style>
